import * as Yup from "yup";
/* eslint-disable no-useless-escape */

import {
  cityRegex,
  cityError,
  websiteError,
  zipCodeError,
  zipCodeRegex,
  passwordError,
  subjectRegex,
  bankNameError,
  bankNameRegex,
  lastNameRegex,
  lastNameError,
  firstNameRegex,
  firstNameError,
  strainNameError,
  strainNameRegex,
  commonNameRegex,
  companyNameError,
  companyNameRegex,
  phoneNumberError,
  phoneNumberRegex,
  hexaDecimalNumericRegex,
} from "./regex";
import { API_CONNECTION_SOFTWARE, EMPLOYEE_ROLE, LABEL_TYPE } from "./enum";

const passRegrex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?!.* ).{8,40}$/;

/**
 * Forget Password Form Validation Schema
 */

const ForgetPasswordSchema = () =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .required("Please fill in the required field")
      .email("Please enter a valid email address"),
  });

/**
 * Sign in Form Validation Schema
 */
const LoginSchema = () =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .required("Please enter this field")
      .email(
        "Invalid Email. Email should be in a format as 'Username@domainname.TLD'",
      )
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Invalid Email. Email should be in a format as 'Username@domainname.TLD'",
      ),
  });

/**
 * Reset Password Form Validation Schema
 */
const SupportSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .required("Please enter this field")
    .email(
      "Invalid Email. Email should be in a format as 'Username@domainname.TLD'",
    )
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid Email. Email should be in a format as 'Username@domainname.TLD'",
    ),
  subject: Yup.string()
    .trim()
    .required("Please enter this field")
    .max(100, "Maximum 100 characters allowed"),
  supportReason: Yup.string().required("Please select a reason"),
  supportMessage: Yup.string()
    .trim()
    .required("Please enter this field")
    .max(250, "Maximum 250 characters allowed"),
  name: Yup.string().trim().required("Please enter this field"),
});

const MySupportSchema = Yup.object().shape({
  name: Yup.string().trim().required("Please enter this field"),
  subject: Yup.string()
    .trim()
    .required("Please enter this field")
    .max(100, "Maximum 100 characters allowed"),
  supportReason: Yup.string().trim().required("Please select a reason"),
  supportMessage: Yup.string()
    .trim()
    .required("Please enter this field")
    .max(1000, "Maximum 1000 characters allowed"),
});

const editMyProfileSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .required("Please enter this field")
    .matches(firstNameRegex, firstNameError)
    .max(50, firstNameError),
  lastName: Yup.string()
    .trim()
    .required("Please enter this field")
    .matches(lastNameRegex, lastNameError)
    .max(50, lastNameError),
});

const ResetPassword = () => {
  return Yup.object().shape({
    newPass: Yup.string()
      .trim()
      .required("Please fill in the required field")
      .matches(passRegrex, passwordError),
    confirmPass: Yup.string()
      .trim()
      .required("Please fill in the required field")
      .oneOf([Yup.ref("newPass")], "Both passwords must match"),
  });
};

/**
 * OTP Configuration 2FA Validation Schema
 */
const OtpSchema = () => {
  return Yup.object().shape({
    otp: Yup.string()
      .trim()
      .required("Please fill in the required field")
      .matches(/^[0-9]/, "OTP must be a number")
      .max(6, "OTP must be 6 digit number")
      .min(6, "OTP must be 6 digit number"),
  });
};

/**
 * Change Password Validation Schema
 */
const ChangePasswordSchema = () => {
  return Yup.object().shape({
    oldPassword: Yup.string().trim().required("Old Password is required"),
    newPassword: Yup.string()
      .trim()
      .required("New password is required")
      .matches(passRegrex, passwordError)
      .notOneOf(
        [Yup.ref("oldPassword")],
        "New password must be different from old password",
      ),
    confirmPassword: Yup.string()
      .trim()
      .required("Confirm password is required")
      .oneOf([Yup.ref("newPassword")], "Both passwords must match"),
  });
};

/**
 * Edit Profile Validation Schema
 */
const EditProfileSchema = () => {
  return Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Please enter your name")
      .matches(/^[A-Za-z ]*$/, "Name must contain only characters"),
    email: Yup.string().trim(),
    mobileNumber: Yup.string()
      .nullable()
      .trim()
      .matches(/^\d+$/, "Mobile Number should contain only numbers")
      .max(15, "Mobile Number should contain 15 digits only.")
      .min(6, "Mobile Number should be atleast 6 digits long.")
      .nullable(),
    status: Yup.boolean(),
    countryCode: Yup.string(),
  });
};

const CompanyProfleEditSchema = () => {
  const validHttpUrl = (value?: string | null) => {
    if (!value) return true;
    const candidate = value.trim();
    if (!candidate) return true;
    const normalized = /^https?:\/\//i.test(candidate)
      ? candidate
      : `https://${candidate}`;
    try {
      // eslint-disable-next-line no-new
      new URL(normalized);
      return true;
    } catch (error) {
      return false;
    }
  };

  const validHttpOrHttpsUrlWithTld = (value?: string | null) => {
    if (!value) return true;
    const candidate = value.trim();
    if (!candidate) return true;
    const normalized = /^https?:\/\//i.test(candidate)
      ? candidate
      : `https://${candidate}`;

    try {
      const parsedUrl = new URL(normalized);
      const hasValidProtocol =
        parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
      const hasValidDomain = /\.[a-z]{2,}$/i.test(parsedUrl.hostname);
      return hasValidProtocol && hasValidDomain;
    } catch (error) {
      return false;
    }
  };

  return Yup.object().shape({
    companyName: Yup.string()
      .trim()
      .required("Please enter this field")
      .matches(companyNameRegex, companyNameError)
      .max(100, companyNameError),

    companyEmail: Yup.string()
      .trim()
      .required("Please enter this field")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Invalid Email. Email should be in a format as 'Username@domainname.TLD'",
      ),
    shippingAddress: Yup.object().shape({
      flatAddress: Yup.string()
        .trim()
        .max(100, "Invalid Flat No., Building, Street")
        .required("Please enter this field"),
      state: Yup.string().trim().required("Please enter this field"),
      city: Yup.string()
        .trim()
        .matches(cityRegex, cityError)
        .required("Please enter this field"),
      zipcode: Yup.string()
        .trim()
        .matches(zipCodeRegex, zipCodeError)
        .required("Please enter this field"),
    }),

    billingAddress: Yup.object().shape({
      flatAddress: Yup.string()
        .trim()
        .max(100, "Invalid Flat No., Building, Street"),
      state: Yup.string().trim(),
      city: Yup.string().trim().matches(cityRegex, cityError),
      zipcode: Yup.string().trim().matches(zipCodeRegex, zipCodeError),
    }),

    companyPicture: Yup.string(),
    bannerImage: Yup.string(),
    countryCode: Yup.string().trim(),
    mobileNo: Yup.string()
      .trim()
      .matches(phoneNumberRegex, phoneNumberError)
      .required("Please enter this field"),
    customLinks: Yup.array().of(
      Yup.object().shape({
        title: Yup.string()
          .trim()
          .max(100, "Title can be at most 100 characters")
          .test(
            "title-required-with-url",
            "Please enter this field",
            function (value) {
              const url = (this.parent?.url ?? "").trim();
              if (!url) return true;
              return Boolean((value ?? "").trim());
            },
          ),
        url: Yup.string()
          .trim()
          .test(
            "url-required-with-title",
            "Please enter this field",
            function (value) {
              const title = (this.parent?.title ?? "").trim();
              if (!title) return true;
              return Boolean((value ?? "").trim());
            },
          )
          .test("valid-url-when-present", websiteError, function (value) {
            const trimmed = (value ?? "").trim();
            if (!trimmed) return true;
            return validHttpOrHttpsUrlWithTld(trimmed);
          }),
        icon: Yup.string().trim().nullable(),
      }),
    ),
    socialLinks: Yup.array()
      .of(
        Yup.object().shape({
          platform: Yup.string().trim().required("Please enter this field"),
          url: Yup.string()
            .trim()
            .required("Please enter this field")
            .max(80, "Invalid Link")
            .test("valid-url", websiteError, validHttpUrl),
        }),
      )
      .max(20),
  });
};
/** Add Employee Schema */
const AddEmployeeSchema = () => {
  return Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required("Please enter this field")
      .matches(firstNameRegex, firstNameError)
      .max(50, firstNameError),
    lastName: Yup.string()
      .trim()
      .required("Please enter this field")
      .matches(lastNameRegex, lastNameError)
      .max(50, lastNameError),
    email: Yup.string()
      .trim()
      .required("Please enter this field")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Invalid Email, Email should be in a format as 'Username@domainname.TLD'",
      ),

    employeeId: Yup.string()
      .matches(
        /^[a-zA-Z0-9.-]*$/,
        "Invalid Employee ID. Only letters, numbers, hyphens (-), and dots (.) are allowed. Max 20 characters.",
      )
      .min(1, "Employee ID must be at least 1 character.")
      .max(20, "Employee ID can have a maximum of 20 characters."),
    role: Yup.string()
      .required("Please enter this field")
      .oneOf(
        [
          EMPLOYEE_ROLE.PACKAGING,
          EMPLOYEE_ROLE.FACILITY_ADMIN,
          EMPLOYEE_ROLE.PLANT_TOUCHING,
          EMPLOYEE_ROLE.NON_PLANT_TOUCHING,
        ],
        "Please select a valid role",
      ),
    permissions: Yup.object().test(
      "at-least-one-selected",
      "At least one permission must be selected",
      (permissions) =>
        permissions &&
        Object.values(permissions).some(
          (permission: any) => permission.view || permission.edit,
        ),
    ),

    facilityLicenseNumberIds: Yup.array()
      .min(1, "Please select a brand")
      .required("Please enter this field"),
  });
};

/**
 * Add Announcement Validation Schema
 */
const AddAnnouncementSchema = () => {
  return Yup.object().shape({
    subject: Yup.string()
      .required("Please enter this field")
      .matches(subjectRegex)
      .max(100, "Invalid Subject, Subject should be of maximum 100 characters"),
    description: Yup.string()
      .trim() // .matches(/^[A-Za-z ]*$/, "Name must contain only characters"),
      .required("Please enter this field")
      .max(
        1000,
        "Invalid Description, Description should be of maximum 1000 characters",
      ),
    scheduleDate: Yup.string().required("Please enter this field"),
    image: Yup.string(),
    minutes: Yup.string().nullable().required("Please select minutes"),
    hour: Yup.string().nullable().required("Please select hour"),
    period: Yup.string().nullable().required("Please select AM/PM"),
  });
};

const AddRewardsSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Please enter this field")
    .matches(
      commonNameRegex,
      "Invalid Reward Name, Reward Name should contain Alphabets, Numbers, (_) & of maximum 100 characters",
    )
    .max(
      100,
      "Invalid Reward Name, Reward Name should contain Alphabets, Numbers, (_) & of maximum 100 characters",
    ),
  type: Yup.string().required("Please select a reward type"),
  ribbonIds: Yup.array()
    .of(Yup.string().required("Each ribbon ID must be a string"))
    .min(1, "Please select Ribbon") // Add this line for minimum length validation
    .required("Please select Ribbon"),
  consumerRedemptionLimit: Yup.number().required("Please enter this field"),
  maxRedemptionLimit: Yup.number().required("Please enter this field"),
  description: Yup.string()
    .trim() // .matches(/^[A-Za-z ]*$/, "Name must contain only characters"),
    .required("Please enter this field")
    .max(
      500,
      "Invalid Description, Description should be of maximum 500 characters",
    ),
});

const AddStrainSchema = Yup.object().shape({
  strainName: Yup.string()
    .trim()
    .required("Please enter this field")
    .matches(strainNameRegex, strainNameError)
    .max(100, strainNameError),
  lineageInput: Yup.string()
    .trim()
    .matches(
      strainNameRegex,
      "Invalid Lineage, Lineage should contain Alphabets, Numbers, Special Characters(!,#,$,&,*,-,.,') & of maximum 100 characters",
    )
    .max(
      100,
      "Invalid Lineage, Lineage should contain Alphabets, Numbers, Special Characters(!,#,$,&,*,-,.,') & of maximum 100 characters",
    ),
  lineage: Yup.array(),
  strainType: Yup.string(),
  feelings: Yup.array()
    .of(Yup.string())
    .max(5, "Maximum 5 feelings can be selected."),
  description: Yup.string()
    .trim()
    .required("Please enter this field")
    .max(
      2000,
      "Invalid Description, Description should be of maximum 2000 characters",
    ),
  imageUrl: Yup.string(),
});

const UploadCsvSchema = Yup.object().shape({
  batchId: Yup.string().nullable(),
  strainName: Yup.string().required("Please select this field"),
  productType: Yup.string().required("Please select this field"),
  harvestDate: Yup.string().required("Please select this field"),
  packagedDate: Yup.string().nullable(),
  productSubType: Yup.string().nullable(),
  facilityLicenseNumber: Yup.string().required("Please select this field"),
  label: Yup.string().required("Please select this field"),
});

const ImportFacilityThroughAPI = Yup.object().shape({
  state: Yup.string().required("Please select this field"),
  externalSoftware: Yup.string().required("Please select this field"),
  apiKey: Yup.string()
    .trim()
    .required("Please select this field")
    .matches(/^(?!https?:\/\/)/i, "Invalid API Key")
    .when("externalSoftware", {
      is: API_CONNECTION_SOFTWARE.LIMS,
      then: (schema) => schema.required("Please enter this field"),
      otherwise: (schema) => schema.notRequired(),
    }),
  secretKey: Yup.string()
    .trim()
    .when("externalSoftware", {
      is: API_CONNECTION_SOFTWARE.LIMS,
      then: (schema) => schema.required("Please enter this field"),
      otherwise: (schema) => schema.notRequired(),
    }),
  nordicCompanyName: Yup.string()
    .trim()
    .required("Please select this field")
    .when("externalSoftware", {
      is: API_CONNECTION_SOFTWARE.NORDIC_ANALYTICAL,
      then: (schema) => schema.required("Please enter this field"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const ImportFacilityThroughAPIInEmployee = Yup.object().shape({
  state: Yup.string().required("Please select this field"),
  apiKey: Yup.string()
    .trim()
    .required("Please select this field")
    .matches(/^(?!https?:\/\/)/i, "Invalid API Key"),
  externalSoftware: Yup.string().required("Please select this field"),
});

const AddImportFacilityNumbers = Yup.object().shape({
  facilityLicenseNumbers: Yup.array()
    .min(1, "At least one selection is required")
    .required("Please select at least one item"),
});

const AddFacilityNumbersManually = Yup.object().shape({
  facilityLicenseInput: Yup.string().when(
    "facilityLicenseNumber",
    (facilityLicenseNumber, schema) => {
      if (!facilityLicenseNumber[0] || facilityLicenseNumber[0].length === 0) {
        return schema
          .matches(/^[a-zA-Z0-9-]*$/, "Invalid Facility License Number")
          .max(20, "Invalid Facility License Number")
          .required("Please enter this field");
      }
      return schema
        .matches(/^[a-zA-Z0-9-]*$/, "Invalid Facility License Number")
        .max(20, "Invalid Facility License Number");
    },
  ),
  facilityLicenseNumber: Yup.array()
    .of(
      Yup.object().shape({
        facilityLicenseNumber: Yup.string(),
        licenseStatus: Yup.string(),
      }),
    )
    .min(1, "Please enter this field")
    .required("Please enter this field"),
});

const LicenseNumberSchema = () => {
  return Yup.object().shape({
    licenseInput: Yup.string().when(
      "licenseNumber",
      (licenseNumber, schema) => {
        return licenseNumber.length === 0
          ? schema
              .matches(/^[a-zA-Z0-9-]*$/, "Invalid License Number")
              .max(20, "Invalid License Number")
              .required("Please enter this field")
          : schema
              .matches(/^[a-zA-Z0-9-]*$/, "Invalid License Number")
              .max(20, "Invalid License Number");
      },
    ),
    licenseNumber: Yup.array(),
  });
};

const DisassociateFacility = () => {
  return Yup.object().shape({
    facilityLicenseNumberIds: Yup.array(),
  });
};

// const ImportBatchSchema = () => {
//   return Yup.object().shape({
//     label: Yup.string()
//       .trim()
//       .required("Please enter this field")
//       .test("length-and-pattern", function (value) {
//         if (value && value.length > 24) {
//           return this.createError({
//             message: "Metrc Label/UID cannot exceed 24 characters",
//           });
//         }
//         if (
//           value &&
//           value.length === 24 &&
//           !/^.{5}[0-9a-fA-F]{2}.*$/.test(value)
//         ) {
//           return this.createError({
//             message:
//               "6th and 7th characters must be hexadecimal (0-9, a-f, A-F)",
//           });
//         }
//         return true;
//       }),
//     licenseNumber: Yup.string()
//       .nullable()
//       .required("Please select a Licence Number"),
//   });
// };

const ImportBatchSchema = () => {
  return Yup.object().shape({
    label: Yup.string()
      .trim()
      .required("Please enter this field")
      .matches(/^[a-zA-Z0-9]*$/, "Special Characters are not allowed") // Restrict special characters
      .test("length-and-pattern", function (value) {
        if (value && value.length > 24) {
          return this.createError({
            message: "Metrc Label/UID cannot exceed 24 characters",
          });
        }

        if (value && value.length >= 7) {
          const sixthAndSeventh = value.substring(5, 7); // Extracting 6th and 7th characters

          if (!/^[0-9a-fA-F]{2}$/.test(sixthAndSeventh)) {
            return this.createError({
              message:
                "6th and 7th characters must be hexadecimal (0-9, a-f, A-F)",
            });
          }
        }

        return true;
      }),
    licenseNumber: Yup.string()
      .nullable()
      .required("Please select a Licence Number"),
  });
};

const AddAccountSchema = () => {
  return Yup.object().shape({
    accountType: Yup.string().trim().required("Please select this field"),
    bankName: Yup.string()
      .trim()
      .required("Please enter this field")
      .matches(bankNameRegex, bankNameError)
      .min(2, "Minimum 2 characters allowed")
      .max(100, bankNameError),
    accountNumber: Yup.string()
      .trim()
      .required("Please enter this field")
      .min(6, "Minimum 6 digits allowed")
      .max(100, "Maximum 17 digits allowed")
      .test(
        "account-not-equal-routing",
        "Bank account number & routing number cannot be the same.",
        function (value) {
          const { routingNumber } = this.parent; // Access the sibling field
          if (value && routingNumber && value === routingNumber) {
            return false; // Fails validation
          }
          return true; // Passes validation
        },
      ),
    routingNumber: Yup.string()
      .trim()
      .required("Please enter this field")
      .length(9, "Invalid Routing Number, Routing Number should be of 9 digits")
      .test(
        "routing-not-equal-account",
        "Bank account number & routing number cannot be the same.",
        function (value) {
          const { accountNumber } = this.parent; // Access the sibling field
          if (value && accountNumber && value === accountNumber) {
            return false; // Fails validation
          }
          return true; // Passes validation
        },
      ),
  });
};

const VerifyBillingEmail = () => {
  return Yup.object().shape({
    billingEmail: Yup.string()
      .trim()
      .required("Please enter this field")
      .email(
        "Invalid Billing Email. Billing Email should be in a format as 'Username@domainname.TLD'",
      ),
  });
};

const AddCoaSchema = Yup.object().shape({
  categories: Yup.string().required("Please select this field"),
  subCategories: Yup.string().required("Please select this field"),
  packageDate: Yup.string().required("Please enter this field"),
  harvestDate: Yup.string().required("Please enter this field"),
});

const serialToNumber = (serial: string): number => {
  if (!serial) return NaN;

  const [hexPart, decPart] = serial.toUpperCase().split("X");

  if (!hexPart || !decPart) return NaN;

  const hexValue = parseInt(hexPart, 16);
  const decValue = parseInt(decPart, 10);

  if (isNaN(hexValue) || isNaN(decValue)) return NaN;

  return hexValue * 10000 + decValue;
};

const ProductTokenizeSchema = Yup.object({
  useStrainImage: Yup.boolean().optional(),

  productImage: Yup.string().when("useStrainImage", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Please enter this field"),
  }),

  growerNote: Yup.string()
    .trim()
    .required("Please enter this field")
    .max(
      1000,
      "Invalid Batch Notes, Batch Notes should be of maximum 1000 characters",
    )
    .matches(
      /^[A-Za-z0-9\s,.;:'"\/?!@#$%&*()\-+=_\n]*$/,
      "Invalid Batch Notes, only Alphanumeric Characters, and Special Characters (,.;:'\"/?!@#$%&*()-+=_) are allowed",
    ),

  labelType: Yup.string().required("Please select label type"),

  // ---------------- PRINT ON DEMAND ----------------

  numberOfLabels: Yup.number()
    .transform((value, originalValue) => {
      // Convert empty string to undefined
      if (originalValue === "") {
        return undefined;
      }
      return Number(originalValue);
    })
    .when("labelType", {
      is: LABEL_TYPE.PRINTONDEMAND,
      then: (schema) =>
        schema
          .required("Please enter Number of Labels")
          .typeError("Only numbers are allowed")
          .integer("Only whole numbers allowed")
          .positive("Number of labels must be greater than 0"),
      otherwise: (schema) => schema.notRequired(),
    }),

  fringe: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "") return undefined;
      return Number(originalValue);
    })
    .when("numberOfLabels", (numberOfLabels, schema) => {
      if (!numberOfLabels) {
        return schema.notRequired();
      }

      return schema.test("max-fringe", function (value) {
        if (!value) return true;

        const maxAllowed = Math.ceil(Number(numberOfLabels) * 0.05);

        if (value > maxAllowed) {
          return this.createError({
            message: `Value exceeds maximum of ${maxAllowed}`,
          });
        }

        return true;
      });
    }),

  // ---------------- DATABIS LABELS ----------------

  qrCodePurchasePackets: Yup.array().when("labelType", {
    is: LABEL_TYPE.DATABIS,
    then: (schema) =>
      schema.min(1, "Please add at least one serial sequence").of(
        Yup.object({
          startingSerialNumber: Yup.string()
            .required("Please enter this field")
            .matches(
              hexaDecimalNumericRegex,
              "Invalid Serial number, Serial numbers should be 3 hexadecimal digits, an X, then 4 numeric digits",
            )
            .test(
              "not-000X0000",
              "Please enter the valid serial number",
              (value) => value !== "000X0000",
            )
            .test(
              "no-overlap-start",
              "Sequence overlaps with an existing range or is duplicated.",
              function (value) {
                if (!value) return true;
                const formValues = this.from?.[1]?.value;
                const packets = formValues?.qrCodePurchasePackets || [];
                const match = this.path.match(/qrCodePurchasePackets\[(\d+)\]/);
                const currentIndex = match ? parseInt(match[1], 10) : -1;
                if (currentIndex === -1) return true;

                const currentStartNum = serialToNumber(value);
                if (isNaN(currentStartNum)) return true;

                // Check if this starting serial number falls inside any other sequence's range
                for (let i = 0; i < packets.length; i++) {
                  if (i === currentIndex) continue;
                  const other = packets[i];
                  if (!other.startingSerialNumber || !other.endingSerialNumber)
                    continue;

                  const start = serialToNumber(other.startingSerialNumber);
                  const end = serialToNumber(other.endingSerialNumber);
                  if (isNaN(start) || isNaN(end)) continue;

                  if (currentStartNum >= start && currentStartNum <= end) {
                    return false;
                  }
                }

                // Also check if we have an ending serial number, does our full range overlap with any other sequence?
                const currentEndSerial = this.parent?.endingSerialNumber;
                if (currentEndSerial) {
                  const currentEndNum = serialToNumber(currentEndSerial);
                  if (
                    !isNaN(currentEndNum) &&
                    currentEndNum >= currentStartNum
                  ) {
                    for (let i = 0; i < packets.length; i++) {
                      if (i === currentIndex) continue;
                      const other = packets[i];
                      if (
                        !other.startingSerialNumber ||
                        !other.endingSerialNumber
                      )
                        continue;

                      const start = serialToNumber(other.startingSerialNumber);
                      const end = serialToNumber(other.endingSerialNumber);
                      if (isNaN(start) || isNaN(end)) continue;

                      if (currentStartNum <= end && currentEndNum >= start) {
                        return false;
                      }
                    }
                  }
                }

                return true;
              },
            ),

          endingSerialNumber: Yup.string()
            .required("Please enter this field")
            .matches(
              hexaDecimalNumericRegex,
              "Invalid Serial number, Serial numbers should be 3 hexadecimal digits, an X, then 4 numeric digits",
            )
            .test(
              "not-000X0000",
              "Please enter the valid serial number",
              (value) => value !== "000X0000",
            )
            .test(
              "no-overlap-end",
              "Sequence overlaps with an existing range or is duplicated.",
              function (value) {
                if (!value) return true;
                const formValues = this.from?.[1]?.value;
                const packets = formValues?.qrCodePurchasePackets || [];
                const match = this.path.match(/qrCodePurchasePackets\[(\d+)\]/);
                const currentIndex = match ? parseInt(match[1], 10) : -1;
                if (currentIndex === -1) return true;

                const currentEndNum = serialToNumber(value);
                if (isNaN(currentEndNum)) return true;

                // Check if this ending serial number falls inside any other sequence's range
                for (let i = 0; i < packets.length; i++) {
                  if (i === currentIndex) continue;
                  const other = packets[i];
                  if (!other.startingSerialNumber || !other.endingSerialNumber)
                    continue;

                  const start = serialToNumber(other.startingSerialNumber);
                  const end = serialToNumber(other.endingSerialNumber);
                  if (isNaN(start) || isNaN(end)) continue;

                  if (currentEndNum >= start && currentEndNum <= end) {
                    return false;
                  }
                }

                // Also check if we have a starting serial number, does our full range overlap with any other sequence?
                const currentStartSerial = this.parent?.startingSerialNumber;
                if (currentStartSerial) {
                  const currentStartNum = serialToNumber(currentStartSerial);
                  if (
                    !isNaN(currentStartNum) &&
                    currentEndNum >= currentStartNum
                  ) {
                    for (let i = 0; i < packets.length; i++) {
                      if (i === currentIndex) continue;
                      const other = packets[i];
                      if (
                        !other.startingSerialNumber ||
                        !other.endingSerialNumber
                      )
                        continue;

                      const start = serialToNumber(other.startingSerialNumber);
                      const end = serialToNumber(other.endingSerialNumber);
                      if (isNaN(start) || isNaN(end)) continue;

                      if (currentStartNum <= end && currentEndNum >= start) {
                        return false;
                      }
                    }
                  }
                }

                return true;
              },
            )
            .when("startingSerialNumber", ([startingSerialNumber], schema) => {
              return schema.test({
                test: function (value: any) {
                  if (!value || !startingSerialNumber) {
                    return true;
                  }

                  const startingSerialHex = startingSerialNumber.slice(0, 3);
                  const endingSerialHex = value.slice(0, 3);

                  const startingSerialNumeric = parseInt(
                    startingSerialNumber.slice(4),
                    10,
                  );
                  const endingSerialNumeric = parseInt(value.slice(4), 10);

                  if (
                    parseInt(endingSerialHex, 16) >
                    parseInt(startingSerialHex, 16)
                  ) {
                    return true;
                  }

                  if (
                    parseInt(endingSerialHex, 16) ===
                    parseInt(startingSerialHex, 16)
                  ) {
                    return endingSerialNumeric >= startingSerialNumeric;
                  }

                  return false;
                },
                message:
                  "Ending serial number should be greater than or equal to the starting serial number",
              });
            }),

          damageSerialNumber: Yup.array().of(
            Yup.string()
              .trim()
              .matches(
                hexaDecimalNumericRegex,
                "Invalid Serial number, Serial numbers should be 3 hexadecimal digits, an X, then 4 numeric digits",
              ),
          ),

          damagedInput: Yup.string()
            .trim()
            .matches(
              hexaDecimalNumericRegex,
              "Invalid Serial number, Serial numbers should be 3 hexadecimal digits, an X, then 4 numeric digits",
            ),
        }),
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
  internalNote: Yup.string()
    .trim()
    .max(
      500,
      "Invalid Internal Note, Internal Note should be of maximum 500 characters",
    )
    .notRequired(),
});

const AddLabelsSchema = Yup.object({
  numberOfLabels: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue),
    )
    .required("Please enter Number of Labels")
    .typeError("Only numbers are allowed")
    .integer("Only whole numbers allowed")
    .positive("Number of labels must be greater than 0"),
  fringe: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue),
    )
    .test("max-fringe", function (value) {
      if (!value) return true;
      const { numberOfLabels } = this.parent;
      if (!numberOfLabels) return true;
      const maxAllowed = Math.ceil(Number(numberOfLabels) * 0.05);
      return value <= maxAllowed;
    })
    .notRequired(),
  internalNote: Yup.string()
    .trim()
    .max(
      500,
      "Invalid Internal Note, Internal Note should be of maximum 500 characters",
    )
    .notRequired(),
});

const AccountListingSchema = Yup.object().shape({
  paymentMethod: Yup.string().required("Please select a payment method"),
  account: Yup.string()
    .nullable()
    .when("paymentMethod", {
      is: "account",
      then: (schema) => schema.required("Please select an account"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const Schema = {
  ForgetPasswordSchema,
  LoginSchema,
  SupportSchema,
  ResetPassword,
  OtpSchema,
  ChangePasswordSchema,
  EditProfileSchema,
  CompanyProfleEditSchema,
  MySupportSchema,
  editMyProfileSchema,
  AddEmployeeSchema,
  AddAnnouncementSchema,
  AddRewardsSchema,
  AddStrainSchema,
  UploadCsvSchema,
  AddImportFacilityNumbers,
  ImportFacilityThroughAPI,
  AddFacilityNumbersManually,
  LicenseNumberSchema,
  DisassociateFacility,
  ImportBatchSchema,
  AddAccountSchema,
  ImportFacilityThroughAPIInEmployee,
  VerifyBillingEmail,
  AddCoaSchema,
  ProductTokenizeSchema,
  AccountListingSchema,
  AddLabelsSchema,
};

export default Schema;
